import { ActionCreatorsMapObject, FunctionType } from "@bookingcom/flights-core";
import { configureStore } from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";
import { useMemo } from "react";

import * as search from "./search/reducer";
import * as searchResults from "./searchResults/reducer";
import * as flexibleDateMinPrice from "./flexibleDateMinPrice/reducer";
import * as searchCriteria from "./searchCriteria/reducer";
import * as flightDetails from "./flightDetails/reducer";
import * as cartDetails from "./cartDetails/reducer";
import * as sanctionScreening from "./sanctionScreening/reducer";
import * as extraProducts from "../ancillaries/store/extraProducts/reducer";
import * as loadingScreen from "./loadingScreen/reducer";
import * as paymentLoadingStatus from "./paymentLoadingStatus/reducer";
import * as order from "./order/reducer";
import * as user from "./user/reducer";
import * as popularFlights from "./popularFlights/reducer";
import * as ariaLive from "./ariaLive/reducer";
import * as metaFlightDetails from "./metaFlightDetails/reducer";
import * as travelPurpose from "./travelPurpose/reducer";
import * as brandedFares from "./brandedFareOffers/reducer";
import * as rebookPolicy from "./rebookPolicy/reducer";
import * as paymentCollection from "./paymentCollection/reducer"; // FLIGHTS_WEB_PAYMENT_COLLECTION_NEW_PAGE
import * as indexCampaigns from "./indexCampaigns/reducer";
import * as cartValidationError from "./cartValidationError/reducer";
import * as flyAnywhere from "./flyAnywhere/reducer";
import * as flightsInterlinking from "./flightsInterlinking/reducer";
import * as searchAiSummary from "./searchAiSummary/reducer";
import * as searchBoxState from "./searchBoxState/reducer";
import * as searchResultsBounds from "./searchResultsBounds/reducer";
import { listenerMiddleware } from "./middlewares";
import {
  whitelabelAncillariesSlice,
  whitelabelAncillariesInitialState
} from "../ancillaries/store/whitelabelAncillaries/whitelabelAncillariesSlice";

declare var window: { __INITIAL_STATE__: Partial<StoreState> };

export const getInitialState = () => ({
  search: search.getInitialState(),
  searchResults: searchResults.getInitialState(),
  searchResultsBounds: searchResultsBounds.getInitialState(),
  flexibleDateMinPrice: flexibleDateMinPrice.getInitialState(),
  searchCriteria: searchCriteria.getInitialState(),
  flightDetails: flightDetails.getInitialState(),
  cartDetails: cartDetails.getInitialState(),
  sanctionScreening: sanctionScreening.getInitialState(),
  extraProducts: extraProducts.getInitialState(),
  order: order.getInitialState(),
  loadingScreen: loadingScreen.getInitialState(),
  paymentLoadingStatus: paymentLoadingStatus.getInitialState(),
  user: user.getInitialState(),
  popularFlights: popularFlights.getInitialState(),
  ariaLive: ariaLive.getInitialState(),
  metaFlightDetails: metaFlightDetails.getInitialState(),
  travelPurpose: travelPurpose.getInitialState(),
  brandedFares: brandedFares.getInitialState(),
  paymentCollection: paymentCollection.getInitialState(), // FLIGHTS_WEB_PAYMENT_COLLECTION_NEW_PAGE
  rebookPolicy: rebookPolicy.getInitialState(),
  indexCampaigns: indexCampaigns.getInitialState(),
  cartValidationError: cartValidationError.getInitialState(),
  flyAnywhere: flyAnywhere.getInitialState(),
  flightsInterlinking: flightsInterlinking.getInitialState(),
  searchAiSummary: searchAiSummary.getInitialState(),
  searchBoxState: searchBoxState.getInitialState(),
  whitelabelAncillaries: whitelabelAncillariesInitialState()
});

export type StoreState = ReturnType<typeof getInitialState>;

export function createStore(initialState: Partial<StoreState> = {}) {
  return configureStore({
    reducer: {
      search: search.reducer,
      searchResults: searchResults.reducer,
      searchResultsBounds: searchResultsBounds.reducer,
      flexibleDateMinPrice: flexibleDateMinPrice.reducer,
      searchCriteria: searchCriteria.reducer,
      flightDetails: flightDetails.reducer,
      cartDetails: cartDetails.reducer,
      sanctionScreening: sanctionScreening.reducer,
      extraProducts: extraProducts.reducer,
      order: order.reducer,
      loadingScreen: loadingScreen.reducer,
      paymentLoadingStatus: paymentLoadingStatus.reducer,
      user: user.reducer,
      popularFlights: popularFlights.reducer,
      ariaLive: ariaLive.reducer,
      metaFlightDetails: metaFlightDetails.reducer,
      travelPurpose: travelPurpose.reducer,
      brandedFares: brandedFares.reducer,
      paymentCollection: paymentCollection.reducer, // FLIGHTS_WEB_PAYMENT_COLLECTION_NEW_PAGE
      rebookPolicy: rebookPolicy.reducer,
      indexCampaigns: indexCampaigns.reducer,
      cartValidationError: cartValidationError.reducer,
      flyAnywhere: flyAnywhere.reducer,
      flightsInterlinking: flightsInterlinking.reducer,
      searchAiSummary: searchAiSummary.reducer,
      searchBoxState: searchBoxState.reducer,
      whitelabelAncillaries: whitelabelAncillariesSlice.reducer
    },
    devTools: {
      name: "Redux store - Main"
    },
    preloadedState: {
      search: search.getInitialState(),
      searchResults: searchResults.getInitialState(),
      searchResultsBounds: searchResultsBounds.getInitialState(),
      flexibleDateMinPrice: flexibleDateMinPrice.getInitialState(),
      searchCriteria: searchCriteria.getInitialState(),
      flightDetails: flightDetails.getInitialState(),
      cartDetails: cartDetails.getInitialState(),
      sanctionScreening: sanctionScreening.getInitialState(),
      extraProducts: extraProducts.getInitialState(),
      order: order.getInitialState(),
      loadingScreen: loadingScreen.getInitialState(),
      paymentLoadingStatus: paymentLoadingStatus.getInitialState(),
      user: user.getInitialState(),
      popularFlights: popularFlights.getInitialState(),
      ariaLive: ariaLive.getInitialState(),
      metaFlightDetails: metaFlightDetails.getInitialState(),
      travelPurpose: travelPurpose.getInitialState(),
      brandedFares: brandedFares.getInitialState(),
      paymentCollection: paymentCollection.getInitialState(), // FLIGHTS_WEB_PAYMENT_COLLECTION_NEW_PAGE
      rebookPolicy: rebookPolicy.getInitialState(),
      indexCampaigns: indexCampaigns.getInitialState(),
      cartValidationError: cartValidationError.getInitialState(),
      flyAnywhere: flyAnywhere.getInitialState(),
      flightsInterlinking: flightsInterlinking.getInitialState(),
      searchAiSummary: searchAiSummary.getInitialState(),
      searchBoxState: searchBoxState.getInitialState(),
      whitelabelAncillaries: whitelabelAncillariesInitialState(),
      ...(process.env.BUILD_TARGET === "client" && window.__INITIAL_STATE__),
      ...initialState
    },
    middleware: (getDefaultMiddleware) => getDefaultMiddleware().prepend(listenerMiddleware.middleware)
  });
}

export type Store = ReturnType<typeof createStore>;
export type RootState = ReturnType<Store["getState"]>;
export type AppDispatch = ReturnType<typeof createStore>["dispatch"];

const getAll = (state: RootState) => state;

/**
 * @deprecated
 * The useStore hook is inefficient, since it will cause your component to re-render whenever ANYTHING in the store
 * changes. Please use the useSelector hook provided by react-redux
 * @see https://gitlab.booking.com/flights/main/-/wikis/Using-the-Redux-Store
 */
export const useStore = (): RootState => useSelector(getAll);

/**
 * @deprecated
 * The useActions hook was part of our old store.
 * Please use the useDispatch hook provided by react-redux
 * @see https://gitlab.booking.com/flights/main/-/wikis/Using-the-Redux-Store
 */
export const useActions = <A extends ActionCreatorsMapObject>(actions: A): A => {
  const dispatch = useDispatch();

  return useMemo(() => {
    const enhancedActions = {};
    Object.entries(actions).forEach(([key, action]) => {
      // @ts-expect-error: Element implicitly has an 'any' type. Fix the issue timely.
      enhancedActions[key] = (...args: any[]) => dispatch(action(...args)) as FunctionType;
    });
    return enhancedActions as A;
  }, [dispatch, actions]);
};
